import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import FooterSection from './FooterSection';
import './LiveClassesPage.css';

export default function LiveClassesPage() {
  const [liveClasses, setLiveClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activePlatformFilter, setActivePlatformFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  // Platform detection helper based on meeting_url
  const detectPlatform = (url = '') => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('meet.google.com')) {
      return { name: 'Google Meet', theme: 'platform-meet', icon: '📹' };
    }
    if (lowerUrl.includes('zoom.us')) {
      return { name: 'Zoom', theme: 'platform-zoom', icon: '🎥' };
    }
    if (lowerUrl.includes('teams.microsoft.com') || lowerUrl.includes('teams.live.com')) {
      return { name: 'MS Teams', theme: 'platform-teams', icon: '👥' };
    }
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
      return { name: 'YouTube Live', theme: 'platform-yt', icon: '▶️' };
    }
    return { name: 'Live Webcast', theme: 'platform-generic', icon: '🌐' };
  };

  // Format date & time helper
  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: 'Ongoing Schedule', time: 'Interactive Slot' };
    const dateObj = new Date(dateStr);
    return {
      date: dateObj.toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      time: dateObj.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  useEffect(() => {
    const fetchLiveClasses = async () => {
      try {
        const response = await fetch('https://metapi.emedhatechnologies.com/api/academic-updates/live-classes');
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          const formatted = result.data.map((item) => {
            const platform = detectPlatform(item.meeting_url);
            const { date, time } = formatDateTime(item.created_at);
            return {
              ...item,
              platform: platform.name,
              platformTheme: platform.theme,
              platformIcon: platform.icon,
              formattedDate: date,
              formattedTime: time
            };
          });
          setLiveClasses(formatted);
        }
      } catch (error) {
        console.error('Error fetching live classes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLiveClasses();
  }, []);

  // Filter items by search query and platform tab
  const filteredClasses = liveClasses.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesPlatform =
      activePlatformFilter === 'All' || item.platform === activePlatformFilter;

    return matchesSearch && matchesPlatform;
  });

  const platforms = ['All', 'Google Meet', 'Zoom', 'MS Teams', 'YouTube Live'];

  return (
    <>
      <Navbar />
      <div className="live-classes-page-wrapper">
        
        {/* 1. Header Hero Banner */}
        <section className="live-hero-banner">
          <div className="live-banner-container">
            <nav className="breadcrumb-nav">
              <Link to="/" className="breadcrumb-link">Home</Link> / <span>Live Classes &amp; Interactive Sessions</span>
            </nav>
            
            <div className="hero-status-pill">
              <span className="live-pulse-dot"></span> Interactive Learning Hub
            </div>
            
            <h1>Live Online Classes &amp; Workshops</h1>
            <p>
              Join real-time lectures, revision workshops, and faculty doubt-clearing sessions designed for distance learners.
            </p>
          </div>
        </section>

        {/* 2. Control Bar (Search & Filter) */}
        <section className="live-controls-section">
          <div className="live-container">
            <div className="live-filter-wrapper">
              
              {/* Search input */}
              <div className="live-search-box">
                <span className="search-symbol">🔍</span>
                <input
                  type="text"
                  placeholder="Search classes by topic, subject, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>
                )}
              </div>

              {/* Platform Pills */}
              <div className="live-platform-pills">
                {platforms.map((platform) => (
                  <button
                    key={platform}
                    className={`platform-pill ${activePlatformFilter === platform ? 'active-pill' : ''}`}
                    onClick={() => setActivePlatformFilter(platform)}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>

            {/* Live Count Indicator */}
            <div className="live-meta-summary">
              {loading ? (
                <span>Fetching real-time timetable...</span>
              ) : (
                <span>
                  Showing <strong>{filteredClasses.length}</strong> active scheduled session{filteredClasses.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* 3. Live Classes Cards Grid */}
        <section className="live-grid-section">
          <div className="live-container">
            {loading ? (
              <div className="live-loading-state">
                <div className="live-spinner"></div>
                <p>Loading scheduled interactive classes...</p>
              </div>
            ) : filteredClasses.length > 0 ? (
              <div className="live-cards-grid">
                {filteredClasses.map((item) => (
                  <article key={item.id} className={`live-card ${item.platformTheme}`}>
                    
                    {/* Top status & platform banner */}
                    <div className="live-card-top">
                      <div className="live-broadcast-indicator">
                        <span className="blink-dot"></span> LIVE CLASSROOM
                      </div>
                      <span className="platform-tag">
                        <span className="tag-icon">{item.platformIcon}</span>
                        {item.platform}
                      </span>
                    </div>

                    <div className="live-card-content">
                      <h3 className="live-title">{item.title}</h3>
                      
                      <p className="live-desc">
                        {item.description || "Join the interactive classroom to participate in live lectures, Q&A discussions, and syllabus revisions."}
                      </p>

                      {/* Time & Meeting Meta Box */}
                      {/* <div className="live-meta-box">
                        <div className="meta-item">
                          <span className="meta-icon">📅</span>
                          <div>
                            <small>Date Scheduled</small>
                            <p>{item.formattedDate}</p>
                          </div>
                        </div>

                        <div className="meta-item">
                          <span className="meta-icon">⏰</span>
                          <div>
                            <small>Session Slot</small>
                            <p>{item.formattedTime}</p>
                          </div>
                        </div>
                      </div> */}

                      {/* Action CTA Link */}
                      <div className="live-action-footer">
                        <a
                          href={item.meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-join-class"
                        >
                          Join Live Classroom <span className="arrow-launch">↗</span>
                        </a>
                      </div>

                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="live-empty-state">
                <div className="empty-icon">📡</div>
                <h3>No live classes found matching your criteria</h3>
                <p>Try clearing your search query or choosing another platform category filter above.</p>
                <button
                  className="btn-reset-filters"
                  onClick={() => { setSearchTerm(''); setActivePlatformFilter('All'); }}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </section>

      </div>
      <FooterSection />
    </>
  );
}